/**
 * PaytableOverlay.jsx — Paytable overlay shell
 *
 * Thin wrapper: backdrop, glow decoration, header/title/separator,
 * close button, data extraction, slide assembly.
 *
 * Carousel engine lives in PaytableSlider.jsx.
 * All config constants live in paytableConfig.js.
 */

import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  useDimensions,
  ASPECT_RATIO_BREAKPOINTS,
} from "../../../../Contexts/DimensionsContext";
import { closePaytable, closeMenu } from "../../../../State/actions";
import { selectPaytableActive } from "../../../../State/selectors";
import {
  selectPaytable,
  selectBet,
  selectBaseBet,
  selectCurrency,
} from "../../../../State/Slices/gameSettingsSlice";

import { PixiSettings } from "../../../../pixiSettings";
import { useSecondaryToolbar } from "../../../../Hooks/useSecondaryToolbar";
import { useIosOverlayGuard } from "../../../../Hooks/useIosOverlayGuard";
import { useOverlayFade } from "../../../../Hooks/useOverlayFade";
import { getUITextureAsDataURL, getGameImageSrc } from "../../../../assets";

import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";

import SpineTextureExtractor from "../../../../utils/SpineTextureExtractor";

import PaytableSlider from "./PaytableSlider";

import {
  BASE_FONT_SIZE,
  CHROME_DEVICE_CONFIG,
  CHROME_RATIOS,
  DESC_FONT_CONFIG,
  getGlobalDescFontMultiplier,
  SLIDE_IDS,
  PAYTABLE_SLIDES,
  SLIDE_CONTENT_SPACED_GAP_MUL,
  STANDARD_MOBILE_LANDSCAPE_TYPO,
  SMALL_MOBILE_PORTRAIT_TYPO,
  FEATURE_SLIDES,
  STACKED_LANDSCAPE_DESC_MAX_W_PCT,
  SPECIAL_SYMBOL_CONFIG,
  SPECIAL_SYMBOL_CARDS,
  SYMBOL_GRID_CONFIG,
  getDeviceConfig,
  getImgWidth,
  getMeasureCanvas,
  measureTextHeight,
  getSymbolGridRows,
  getSymbolIdByName,
  splitPortraitGrid,
  getPayValues,
  getPaytableArrowLayout,
  PAYTABLE_TUNED_LANDSCAPE_CHROME,
  isBrowserChromeShortenedPortrait,
  getPaytableTunedLandscapeSlideAreaPad,
  getPaytableTunedLandscapeSideOffset,
  getPaytableLayoutDevice,
} from "./paytableConfig";

// Feature-slide description fit-to-height safety margin — target this fraction
// of the available height so descenders aren't clipped by the container.
const FEATURE_DESC_FILL_SAFETY = 0.98;
// Portrait stacked layout — canvas measure runs slightly tall vs DOM, so allow
// a touch more fill before stepping down (reclaim pass still adds up to 1px).
const FEATURE_DESC_FILL_SAFETY_PORTRAIT = 0.99;

// ── Component ──

const PaytableOverlay = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const paytableActive = useSelector(selectPaytableActive);
  const paytable = useSelector(selectPaytable);
  const bet = useSelector(selectBet);
  const baseBet = useSelector(selectBaseBet);
  const currency = useSelector(selectCurrency);

  const {
    dimensions,
    device,
    orientation,
    uiSymbolScale,
    uiSymbolSize,
    isMobileSmall,
  } = useDimensions();
  const toolbar = useSecondaryToolbar();
  // Tablet uses MOBILE layout keys — only uiSymbolScale/viewport size differs.
  // Desktop adopts the standard-mobile-landscape baseline (mirrored via
  // isMirroredMobileLandscape below), so it also reads MOBILE layout keys.
  const isDesktop = device === PixiSettings.DEVICE.DESKTOP;
  // Wide desktop is a refinement of desktop: it inherits every desktop layout
  // branch and only overrides the values gated on isWideDesktop below. Derived
  // straight from dimensions (like useSecondaryToolbar) — the context collapses
  // all desktop widths to DEVICE.DESKTOP, so we read the width tier here.
  // Covers wide + ultra-wide (both ≥ WIDE_DESKTOP_MIN_WIDTH, 2540px).
  const isWideDesktop =
    isDesktop &&
    dimensions.width >= ASPECT_RATIO_BREAKPOINTS.WIDE_DESKTOP_MIN_WIDTH;
  const layoutDevice = isDesktop
    ? PixiSettings.DEVICE.MOBILE
    : getPaytableLayoutDevice(device);

  const { isHidden } = useIosOverlayGuard();

  // Mount + fade lifecycle — shared hook (animejs on the RAF ticker, no timers).
  const { shouldRender, overlayRef } = useOverlayFade({
    active: paytableActive,
    isHidden,
  });

  // Re-measure trigger. The fit effects below read container sizes from the
  // DOM, but on a drag-resize the containers lag the viewport by a frame, so a
  // synchronous measure runs against an unsettled size and never corrects
  // (fine on reload — a single settled measure). Bumping this on the frame
  // after any dimensions change forces one more measure pass once layout has
  // settled; the state-change guards make it a no-op when nothing moved.
  const [measureTick, setMeasureTick] = useState(0);
  useEffect(() => {
    if (!shouldRender) return undefined;
    const raf = requestAnimationFrame(() => setMeasureTick((t) => t + 1));
    return () => cancelAnimationFrame(raf);
  }, [shouldRender, dimensions.width, dimensions.height]);

  // ── Responsive sizing — Single chrome computation for Overlay + Slider ──
  const isPortrait =
    (device === PixiSettings.DEVICE.MOBILE ||
      device === PixiSettings.DEVICE.TABLET) &&
    orientation === PixiSettings.ORIENTATION.PORTRAIT;

  const chrome = useMemo(() => {
    const config = getDeviceConfig(
      CHROME_DEVICE_CONFIG,
      layoutDevice,
      orientation,
    );
    const base = BASE_FONT_SIZE * config.fontMultiplier * uiSymbolScale;
    return {
      title: Math.round(base * CHROME_RATIOS.title),
      subtitle: Math.round(base * CHROME_RATIOS.subtitle),
      arrow: Math.round(
        base * CHROME_RATIOS.arrow * (config.arrowScale || 1.0),
      ),
      close: Math.round(base * CHROME_RATIOS.close),
      dot: Math.round(base * CHROME_RATIOS.dot),
      separator: Math.round(base * CHROME_RATIOS.separatorHeight),
      topPad: Math.round(base * CHROME_RATIOS.topPad),
      base,
    };
  }, [layoutDevice, orientation, uiSymbolScale]);

  // ── Shell layout — standard mobile portrait aligned with GameRulesOverlay ──
  const isTablet = device === PixiSettings.DEVICE.TABLET;
  const isStandardMobile =
    device === PixiSettings.DEVICE.MOBILE && !isMobileSmall;
  // Toolbar-shortened viewport → small-mobile portrait shell. Fullscreen tall
  // phones (game height ≈ screen height) keep standard-mobile padding even when
  // visible CSS height is under SHORT_PORTRAIT_HEIGHT. See
  // isBrowserChromeShortenedPortrait in paytableConfig.js.
  const isShortStandardPortrait =
    isStandardMobile &&
    isPortrait &&
    isBrowserChromeShortenedPortrait(dimensions.height);
  const isStandardMobilePortrait =
    isStandardMobile && isPortrait && !isShortStandardPortrait;
  const isStandardMobileLandscape = isStandardMobile && !isPortrait;
  const isTabletPortrait = isTablet && isPortrait;
  const isTabletLandscape = isTablet && !isPortrait;
  const isSmallMobilePortrait =
    isPortrait && (isMobileSmall || isShortStandardPortrait);
  const isSmallMobileLandscape = isMobileSmall && !isPortrait;
  const isTunedStandardPortrait = isStandardMobilePortrait || isTabletPortrait;
  // Tablet landscape and desktop both mirror standard mobile landscape exactly
  // (0 layout diffs); desktop then tunes via desktop* config knobs.
  const isMirroredMobileLandscape =
    isStandardMobileLandscape || isTabletLandscape || isDesktop;
  const isTunedMobileLandscape =
    isMirroredMobileLandscape || isSmallMobileLandscape;
  const isTunedShellPortrait = isTunedStandardPortrait || isSmallMobilePortrait;

  const shellTopPad = isStandardMobilePortrait
    ? chrome.topPad * 7.5
    : isTabletPortrait
      ? chrome.topPad * 0.8
      : isTabletLandscape
        ? chrome.topPad * 1.4 * 6.5
        : isWideDesktop
          ? chrome.topPad *
            PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopShellTopPadMul
          : isDesktop
            ? chrome.topPad * 1.4
            : isTunedMobileLandscape
            ? chrome.topPad * 1.6
            : isSmallMobilePortrait
              ? chrome.topPad * 0.8
              : Math.round(chrome.topPad * 1.3);

  const shellBottomPad = isStandardMobilePortrait
    ? chrome.topPad * 7.75
    : isTabletPortrait
      ? chrome.topPad * 1.5
      : isTabletLandscape
        ? chrome.topPad * 3.3 * 3.35
        : isWideDesktop
          ? chrome.topPad *
            PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopShellBottomPadMul
          : isTunedMobileLandscape
          ? chrome.topPad * 3.3
          : isSmallMobilePortrait
            ? chrome.topPad * 1.5
            : isPortrait
              ? chrome.topPad * 3.8
              : chrome.topPad * 3.2;

  // Content width is a fixed multiple of the (viewport-fit) symbol size, so it
  // scales continuously with the layout — no absolute cap. uiSymbolSize is
  // already fit to the viewport by DimensionsContext (its scale is the min of
  // width-fit / height-fit / scaleCap), so a multiple of it can never overflow;
  // when the window shrinks the symbols shrink and the popup shrinks with them.
  const contentWidthSymbolMul = isWideDesktop
    ? PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopContentWidthSymbolMul
    : isDesktop
    ? PAYTABLE_TUNED_LANDSCAPE_CHROME.desktopContentWidthSymbolMul
    : isSmallMobileLandscape
      ? PAYTABLE_TUNED_LANDSCAPE_CHROME.smallMobileLandscapeContentWidthSymbolMul
      : isTabletLandscape
        ? PAYTABLE_TUNED_LANDSCAPE_CHROME.tabletLandscapeContentWidthSymbolMul
        : PAYTABLE_TUNED_LANDSCAPE_CHROME.standardMobileLandscapeContentWidthSymbolMul;
  const tunedMobileLandscapeShellWidth = isTunedMobileLandscape
    ? Math.round(uiSymbolSize * contentWidthSymbolMul)
    : null;

  const tabletPortraitContentWidth = isTabletPortrait
    ? dimensions.width * 0.5
    : null;

  const shellContentWidth = isStandardMobilePortrait
    ? dimensions.width * 0.75
    : isTabletPortrait
      ? tabletPortraitContentWidth
      : isSmallMobilePortrait
        ? dimensions.width * 0.8
        : (tunedMobileLandscapeShellWidth ?? undefined);

  const separatorLineWidth = isStandardMobilePortrait
    ? Math.min(chrome.title * 12, dimensions.width * 0.95)
    : isTabletPortrait
      ? tabletPortraitContentWidth
      : isTunedMobileLandscape
        ? tunedMobileLandscapeShellWidth
        : isPortrait
          ? Math.min(chrome.title * 12, dimensions.width * 0.92)
          : Math.min(chrome.title * 16, dimensions.width * 0.65);

  const separatorHeight = isTunedStandardPortrait
    ? Math.round(chrome.separator * 1.25)
    : isWideDesktop
      ? Math.round(
          chrome.separator *
            PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopSeparatorMul,
        )
      : isDesktop
      ? Math.round(chrome.separator * 1.0)
      : isTunedMobileLandscape
        ? Math.round(chrome.separator * 1.2)
        : chrome.separator;

  const closeButtonSize = isTunedMobileLandscape
    ? Math.round(chrome.close * PAYTABLE_TUNED_LANDSCAPE_CHROME.closeMul)
    : chrome.close;

  const tunedLandscapeSlideAreaPad =
    isTunedMobileLandscape && shellContentWidth != null
      ? getPaytableTunedLandscapeSlideAreaPad(
          dimensions.width,
          shellContentWidth,
        )
      : null;
  const tunedLandscapeChromeSideOffset =
    tunedLandscapeSlideAreaPad != null
      ? getPaytableTunedLandscapeSideOffset(chrome, tunedLandscapeSlideAreaPad)
      : null;

  const closeButtonRight = isPortrait
    ? Math.max(6, Math.round(chrome.close * 0.12))
    : tunedLandscapeChromeSideOffset != null
      ? Math.max(4, tunedLandscapeChromeSideOffset - Math.round(chrome.close * 0.35))
      : shellContentWidth != null
        ? Math.max(
            4,
            Math.round((dimensions.width - shellContentWidth) / 2) -
              Math.round(chrome.close * 0.8),
          )
        : Math.round(
            (dimensions.width - separatorLineWidth) / 2 - chrome.close * 3.0,
          );

  const linePad = Math.round(chrome.title * 0.5);
  const slideContentGap = isTunedMobileLandscape
    ? Math.round(linePad * 1.5)
    : linePad;
  const slideContentSpacedGapMul = isTunedStandardPortrait
    ? SLIDE_CONTENT_SPACED_GAP_MUL.mobilePortrait
    : isSmallMobilePortrait
      ? SLIDE_CONTENT_SPACED_GAP_MUL.mobileSmallPortrait
      : isTunedMobileLandscape
        ? SLIDE_CONTENT_SPACED_GAP_MUL.mobileLandscape
        : SLIDE_CONTENT_SPACED_GAP_MUL.default;
  const slideContentSpacedGap = isWideDesktop
    ? Math.round(
        linePad * PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopSlideContentSpacedGapMul,
      )
    : isDesktop
    ? Math.round(linePad * 1.0)
    : isTunedMobileLandscape
      ? slideContentGap
      : Math.round(linePad * slideContentSpacedGapMul);
  const separatorMarginTop = isTunedStandardPortrait
    ? Math.round(linePad * 0.6)
    : isSmallMobilePortrait
      ? Math.round(linePad * 0.6)
      : 0;
  const separatorMarginBottom = isTunedStandardPortrait
    ? Math.round(linePad * 0.6)
    : isSmallMobilePortrait
      ? Math.round(linePad * 0.6)
      : isTunedMobileLandscape
        ? Math.round(linePad * 0.6)
        : 0;
  const footerMarginTop = isTunedMobileLandscape
    ? Math.round(linePad * 1.0)
    : separatorMarginBottom;
  const headerPaddingBottom = isTunedShellPortrait
    ? 0
    : Math.round(linePad * 0.25);

  const landscapeUniformTypo = isTunedMobileLandscape
    ? STANDARD_MOBILE_LANDSCAPE_TYPO
    : null;
  const landscapeFixedDescFont = landscapeUniformTypo?.fixedDescFont ?? false;
  const uniformDescFontMul = landscapeUniformTypo?.descFontMul ?? null;
  const uniformFeatureSubtitleMul =
    landscapeUniformTypo?.featureSubtitleMul ?? null;

  // Global description font multiplier — one number for the current device/
  // orientation tier + locale, layered on top of every slide's own descFontMul.
  // See getGlobalDescFontMultiplier in paytableConfig.js for the full rationale.
  const descFontTierKey = isSmallMobilePortrait
    ? "mobileSmallPortrait"
    : isSmallMobileLandscape
      ? "mobileSmallLandscape"
      : isStandardMobilePortrait
        ? "mobilePortrait"
        : isStandardMobileLandscape
          ? "mobileLandscape"
          : isTabletPortrait
            ? "tabletPortrait"
            : isTabletLandscape
              ? "tabletLandscape"
              : isWideDesktop
                ? "wideDesktop"
                : "desktop";
  const globalDescFontMultiplier = useMemo(
    () => getGlobalDescFontMultiplier(descFontTierKey, i18n.language),
    [descFontTierKey, i18n.language],
  );

  const getSlideDescFontMul = useCallback(
    (defaultMul = 0.8) =>
      (uniformDescFontMul ?? defaultMul) * globalDescFontMultiplier,
    [uniformDescFontMul, globalDescFontMultiplier],
  );
  const smallMobilePortraitSubtitleMul = isSmallMobilePortrait
    ? SMALL_MOBILE_PORTRAIT_TYPO.subtitleFontMul
    : 1;
  const getUniformFeatureSubtitleH = useCallback(
    (measureMul = 1) =>
      uniformFeatureSubtitleMul != null
        ? Math.round(
            chrome.subtitle * 0.7 * uniformFeatureSubtitleMul * measureMul,
          )
        : Math.round(
            chrome.subtitle * 0.7 * smallMobilePortraitSubtitleMul * measureMul,
          ),
    [
      uniformFeatureSubtitleMul,
      chrome.subtitle,
      smallMobilePortraitSubtitleMul,
    ],
  );

  const headerTitleFontSize = isTunedStandardPortrait
    ? Math.round(chrome.title * 0.94)
    : isWideDesktop
      ? Math.round(
          chrome.title *
            PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopHeaderTitleMul,
        )
      : isDesktop
      ? Math.round(
          chrome.title * PAYTABLE_TUNED_LANDSCAPE_CHROME.desktopHeaderTitleMul,
        )
      : isTunedMobileLandscape
        ? Math.round(
            chrome.title * PAYTABLE_TUNED_LANDSCAPE_CHROME.headerTitleMul,
          )
        : chrome.title;

  const slideTitleFontSize = isWideDesktop
    ? Math.round(
        chrome.subtitle *
          PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopSlideTitleMul,
      )
    : isDesktop
    ? Math.round(
        chrome.subtitle * PAYTABLE_TUNED_LANDSCAPE_CHROME.desktopSlideTitleMul,
      )
    : isTunedMobileLandscape
      ? Math.round(
          chrome.subtitle * PAYTABLE_TUNED_LANDSCAPE_CHROME.slideTitleMul,
        )
      : chrome.subtitle;

  const featureSubtitleFontSize = isTunedMobileLandscape
    ? getUniformFeatureSubtitleH()
    : Math.round(chrome.subtitle * 0.7 * smallMobilePortraitSubtitleMul);

  const glowTop = isTunedStandardPortrait ? "-12%" : "-8%";
  const glowWidth = isTunedStandardPortrait ? "150%" : "120%";

  const arrowLayout = getPaytableArrowLayout(chrome);
  const arrowSafeZone = arrowLayout.safeGutter * 2;

  const handleClose = () => {
    soundManager.play(SOUND_NAME.button_click);
    dispatch(closePaytable());
    dispatch(closeMenu());
  };

  // ── Build slides array ──
  // Each entry is a render function () => JSX. The slider wraps each in <PaytableSlide>.

  // Extract all spine data URLs (used by the symbols grid)
  const spineDataURLs = useMemo(() => {
    return SpineTextureExtractor.getDataURLs() || {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRender]);

  // Symbols grid data: the top row and the low row of SYMBOL_GRID_ROWS
  const symbolGridData = useMemo(() => {
    const rows = getSymbolGridRows();
    const buildSection = (symbols) =>
      symbols.map((s) => ({
        id: s.id,
        name: s.name,
        imgSrc: spineDataURLs[s.name]?.src || null,
        imgW: spineDataURLs[s.name]?.width || 0,
        imgH: spineDataURLs[s.name]?.height || 0,
        payValues: getPayValues(paytable, s.id, bet, baseBet),
      }));

    return {
      top: buildSection(rows.top),
      low: buildSection(rows.low),
    };
  }, [spineDataURLs, paytable, bet, baseBet]);

  // Pay values per Wild & Scatters card; empty for a card without `showPays`
  const specialCardPays = useMemo(
    () =>
      SPECIAL_SYMBOL_CARDS.map((card) => {
        const id = card.showPays ? getSymbolIdByName(card.symbolName) : null;
        return id == null ? [] : getPayValues(paytable, id, bet, baseBet);
      }),
    [paytable, bet, baseBet],
  );

  // ── Game image cache — extract all data URLs once when overlay mounts ──
  // Same pattern as primary PaytableSection.symbolImages: useMemo runs once,
  // all slides read from the map — zero canvas work during slider transitions.
  const gameImages = useMemo(() => {
    if (!shouldRender) return {};
    // The map is built from these names ONLY: anything a slide names in
    // `assetName` has to appear here too, or it silently draws an empty box.
    const names = ["paylines_portrait", "paylines_landscape", "glow"];
    const result = {};
    for (const name of names) {
      result[name] = getGameImageSrc(name);
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRender]);

  // Payline image — picked from cache based on current orientation
  const paylineImgSrc =
    gameImages[isPortrait ? "paylines_portrait" : "paylines_landscape"] || "";

  // ── Dynamic font size for feature descriptions ──
  // Single path for all devices — ratios from DESC_FONT_CONFIG.
  const fontCfg = getDeviceConfig(DESC_FONT_CONFIG, layoutDevice, orientation);
  const smallMobilePortraitDescMul = isSmallMobilePortrait
    ? SMALL_MOBILE_PORTRAIT_TYPO.descFontMul
    : 1;
  const descBaseFontSize = Math.round(
    chrome.base * fontCfg.baseMul * smallMobilePortraitDescMul,
  );
  // Small-mobile-landscape description shrink — shared by the feature slides
  // (baked into uniformDescFontSize) and the Wild & Scatters cards so
  // every description renders at the same size on this breakpoint.
  const smallMobileLandscapeDescMul = isSmallMobileLandscape
    ? (landscapeUniformTypo?.smallMobileLandscapeDescFontMul ?? 1)
    : 1;
  const uniformDescFontSize = landscapeUniformTypo
    ? Math.round(
        descBaseFontSize *
          landscapeUniformTypo.descFontMul *
          smallMobileLandscapeDescMul,
      )
    : null;
  // ── Refs for measuring feature slide body containers (portrait + landscape) ──
  const featureBodyRefs = useRef([]);
  const [featureFontSizes, setFeatureFontSizes] = useState(null);

  // ── Dynamic feature text sizing — measures actual container from DOM on every resize ──
  // Uses useLayoutEffect to read real container dimensions from refs, then steps down
  // font size until text fits. Runs before paint so there's no visual flash.
  useLayoutEffect(() => {
    if (!shouldRender) {
      setFeatureFontSizes((prev) => (prev === null ? prev : null));
      return;
    }

    const hasRefs = featureBodyRefs.current.some((el) => el != null);
    if (!hasRefs) return;

    const canvas = getMeasureCanvas();
    const ctx = canvas.getContext("2d");
    const lp = Math.round(chrome.title * 0.5);

    const newSizes = FEATURE_SLIDES.map((f, idx) => {
      const bodyEl = featureBodyRefs.current[idx];
      const baseFont = Math.round(
        descBaseFontSize * getSlideDescFontMul(f.descFontMul),
      );

      if (!bodyEl) {
        return {
          fontSize: isPortrait
            ? baseFont
            : landscapeFixedDescFont
              ? uniformDescFontSize
              : descBaseFontSize,
          descWidths: [],
        };
      }

      // Small mobile landscape keeps the fixed uniform desc size (its
      // uniformDescFontSize already bakes in the extra shrink). Standard
      // mobile landscape, tablet landscape, and desktop fall through to the
      // measure-and-fit path below so long translations can't overflow.
      if (!isPortrait && isSmallMobileLandscape && uniformDescFontSize != null) {
        return { fontSize: uniformDescFontSize, descWidths: [] };
      }

      const descText = t(f.desc);
      let textW;
      let textAvailH;

      // ── Which LAYOUT, not which orientation ──
      //
      // This used to test isPortrait, which was the same question until the
      // column was opened up to landscape. A landscape column then fell into
      // the side-by-side branch below and measured `.spt-feature-text-wrap` —
      // an element the column does not render — so it silently used the
      // half-width fallback and sized the text for a box it does not have.
      const isStackedLayout = isPortrait || f.stackedAlways === true;

      if (isStackedLayout) {
        // Stacked layout: subtitle → image → description (measure image from DOM).
        const stackedBodyEl = bodyEl.querySelector(".spt-feature-stacked-body");
        const imgWrapEl = stackedBodyEl?.querySelector(".spt-feature-img-wrap");
        const imageH = imgWrapEl?.offsetHeight ?? 0;
        const stackedBodyH = stackedBodyEl?.clientHeight ?? bodyEl.clientHeight;
        // The landscape column caps the description and has no gaps; portrait
        // fills its width and keeps linePad between the image and the text.
        textW = isPortrait
          ? bodyEl.clientWidth
          : Math.round(
              bodyEl.clientWidth * (STACKED_LANDSCAPE_DESC_MAX_W_PCT / 100),
            );
        textAvailH = Math.max(
          0,
          stackedBodyH - imageH - (isPortrait ? lp : 0),
        );
      } else {
        // Side-by-side layout: image left, subtitle + description right.
        const bodyH = bodyEl.clientHeight;
        const bodyW = bodyEl.clientWidth;
        const textWrapEl = bodyEl.querySelector(".spt-feature-text-wrap");
        const descPad = chrome.base * 0.3;
        textW = textWrapEl
          ? textWrapEl.clientWidth
          : (bodyW * 0.5 - descPad * 2) * 0.85;

        const subtitleEl = bodyEl.querySelector(".spt-feature-subtitle");
        const subtitleH =
          subtitleEl?.offsetHeight ??
          Math.round(getUniformFeatureSubtitleH(1.2));
        const paddingTop = isTunedMobileLandscape
          ? 0
          : Math.round(lp * (f.textPadMul != null ? f.textPadMul : 1.5));
        const textWrapGap = isTunedMobileLandscape
          ? Math.round(lp * 0.4)
          : Math.round(lp * 0.5);
        textAvailH = Math.max(
          0,
          bodyH - subtitleH - paddingTop - textWrapGap,
        );
      }

      // Fit-to-height (same approach as wild / coin slides): start at the shared
      // base size, then step down until the description fits.
      const fillSafety = isPortrait
        ? FEATURE_DESC_FILL_SAFETY_PORTRAIT
        : FEATURE_DESC_FILL_SAFETY;
      const maxTextH = textAvailH * fillSafety;
      let fontSize = baseFont;
      let designTextH = measureTextHeight(ctx, descText, fontSize, textW);
      while (designTextH > maxTextH && fontSize > 6) {
        fontSize -= 1;
        designTextH = measureTextHeight(ctx, descText, fontSize, textW);
      }
      // Portrait: canvas wrapping is slightly taller than the DOM — reclaim 1px
      // when the full container height can still fit it (avoids over-shrinking).
      if (isPortrait && fontSize < baseFont) {
        const bumped = fontSize + 1;
        const bumpedH = measureTextHeight(ctx, descText, bumped, textW);
        if (bumpedH <= textAvailH) {
          fontSize = bumped;
        }
      }

      return { fontSize, descWidths: [] };
    });

    // Only update state if values actually changed (prevents infinite re-render loop)
    setFeatureFontSizes((prev) => {
      if (
        prev &&
        prev.length === newSizes.length &&
        prev.every(
          (v, i) =>
            v.fontSize === newSizes[i].fontSize &&
            JSON.stringify(v.descWidths) ===
              JSON.stringify(newSizes[i].descWidths),
        )
      ) {
        return prev;
      }
      return newSizes;
    });
  }, [
    shouldRender,
    isPortrait,
    isSmallMobileLandscape,
    isTunedMobileLandscape,
    dimensions,
    descBaseFontSize,
    uniformDescFontSize,
    landscapeFixedDescFont,
    chrome,
    t,
    uniformFeatureSubtitleMul,
    getUniformFeatureSubtitleH,
    getSlideDescFontMul,
    measureTick,
  ]);

  // ── Refs for measuring slide content containers ──
  const specialSectionsRef = useRef(null);
  const gridContentRef = useRef(null);
  const [slideSizes, setSlideSizes] = useState(null);

  // ── Dynamic sizing for the symbol slides — measures actual containers from DOM on every resize ──
  useLayoutEffect(() => {
    if (!shouldRender) return;

    const canvas = getMeasureCanvas();
    const ctx = canvas.getContext("2d");
    const lp = Math.round(chrome.title * 0.5);

    // ── Wild & Scatters slide — uniform scale, integer pixels ──
    let special = {
      imgH: 100,
      fontSize: Math.round(
        descBaseFontSize *
          getSlideDescFontMul(SPECIAL_SYMBOL_CONFIG.descFontMul),
      ),
    };
    if (specialSectionsRef.current) {
      const el = specialSectionsRef.current;
      const containerH = el.clientHeight;
      const containerW = el.clientWidth;
      const numSections = SPECIAL_SYMBOL_CARDS.length;

      let sectionH, textW;
      if (isPortrait) {
        const betweenGaps = Math.round(lp * 1.6) * (numSections - 1);
        sectionH = (containerH - betweenGaps) / numSections;
        textW = containerW * 0.75;
      } else if (isTunedMobileLandscape) {
        sectionH = containerH;
        const sectionGap = lp;
        textW = (containerW - sectionGap * (numSections - 1)) / numSections;
      } else {
        sectionH = containerH;
        const descPad = chrome.base * 0.3 * 2;
        textW = (containerW / numSections - descPad) * 0.6;
      }

      const imgFraction = isPortrait
        ? (SPECIAL_SYMBOL_CONFIG.imgFraction?.portrait ?? 0.5)
        : isTunedMobileLandscape
          ? (SPECIAL_SYMBOL_CONFIG.imgFraction?.landscape ?? 0.38) *
            (SPECIAL_SYMBOL_CONFIG.standardMobileLandscapeImgFractionMul ?? 1)
          : (SPECIAL_SYMBOL_CONFIG.imgFraction?.landscape ?? 0.38);
      const designImgH = Math.round(sectionH * imgFraction);
      const designSubtitleH = getUniformFeatureSubtitleH();
      const designDescFont = Math.round(
        descBaseFontSize *
          getSlideDescFontMul(SPECIAL_SYMBOL_CONFIG.descFontMul),
      );
      const gap = Math.round(lp * 0.6);

      const imgScaleMul = SPECIAL_SYMBOL_CONFIG.imgScaleMul ?? 1;
      const maxDesignImgH = Math.max(
        ...SPECIAL_SYMBOL_CARDS.map((s) => {
          const sc =
            ((typeof s.imgScale === "object"
              ? getImgWidth(s.imgScale, layoutDevice, isPortrait)
              : s.imgScale) || 1) * imgScaleMul;
          return Math.round(designImgH * sc);
        }),
      );

      // Only image, pay values and description scale; subtitle + gaps are
      // fixed. Fit the scalable parts into the height left after that overhead.
      const payLineH = designDescFont * SPECIAL_SYMBOL_CONFIG.payLineHeight;
      const perSection = SPECIAL_SYMBOL_CARDS.map((s, si) => {
        const payLines = specialCardPays[si]?.length ?? 0;
        const fixedOverhead =
          designSubtitleH * 1.3 + gap * (payLines > 0 ? 3 : 2);
        const textH =
          measureTextHeight(ctx, t(s.desc), designDescFont, textW) +
          payLines * payLineH;
        const scalableH = maxDesignImgH + textH;
        return isSmallMobileLandscape
          ? Math.min(1, sectionH / (scalableH + fixedOverhead))
          : Math.min(
              1,
              Math.max(0, sectionH * FEATURE_DESC_FILL_SAFETY - fixedOverhead) /
                Math.max(1, scalableH),
            );
      });
      const scale = Math.min(...perSection);

      const scaledFontSize = isSmallMobileLandscape
        ? Math.round(designDescFont * smallMobileLandscapeDescMul)
        : Math.round(designDescFont * scale);
      special = {
        imgH: Math.round(designImgH * scale),
        fontSize: scaledFontSize,
        ...(isTunedMobileLandscape
          ? {}
          : {
              descWidths: SPECIAL_SYMBOL_CARDS.map(() =>
                Math.round(scaledFontSize * 18),
              ),
            }),
      };
    }

    // ── Symbols grid slide — integer pixels, continuous scale ──
    let grid = {
      imgMaxH: 60,
      lpImgMaxH: 50,
      pvFontSize: 12,
    };
    if (gridContentRef.current) {
      const el = gridContentRef.current;
      const availH = el.clientHeight;
      const availW = el.clientWidth;
      const allSymbols = [...symbolGridData.top, ...symbolGridData.low];
      const maxPayLines = (symbols) =>
        Math.max(1, ...symbols.map((sym) => sym.payValues?.length ?? 0));

      if (isPortrait) {
        // Budget what the grid really draws: the CSS row gap, the block gap
        // before a three-column row, and each cell's picture-to-values gap.
        const block2 = el.querySelector(".spt-grid-block-2col");
        const firstCell = el.querySelector(".spt-grid-cell");
        const rowGapPx = block2
          ? parseFloat(getComputedStyle(block2).rowGap) || 0
          : Math.round(lp * 0.3);
        const contentGapPx =
          parseFloat(getComputedStyle(el).rowGap) || rowGapPx;
        const cellGapPx = firstCell
          ? parseFloat(getComputedStyle(firstCell).rowGap) || 0
          : 0;
        const { twoCol, threeCol } = splitPortraitGrid(allSymbols);
        const twoColRows = Math.ceil(twoCol.length / 2);
        const threeColRows = threeCol.length > 0 ? 1 : 0;
        const portraitRows = Math.max(1, twoColRows + threeColRows);
        const totalGaps =
          rowGapPx * Math.max(0, twoColRows - 1) +
          (twoColRows > 0 && threeColRows ? contentGapPx : 0);
        const rowH = Math.round(
          Math.max(0, (availH - totalGaps) / portraitRows),
        );

        const basePvFont = Math.round(chrome.base * 0.82);
        const pvFontSize =
          basePvFont + (SYMBOL_GRID_CONFIG.portraitPvFontSizeOffset ?? 0);
        // Every row is laid out at the same height, so the budget covers the
        // row with the most pay lines.
        const maxPvLines = maxPayLines(allSymbols);
        // The pay-value line's real height per px of font, measured off a
        // rendered one ("normal" line-height is not 1.2).
        const pvEl = el.querySelector(".spt-grid-pv");
        const pvElFont = pvEl ? parseFloat(getComputedStyle(pvEl).fontSize) : 0;
        const pvLineRatio =
          pvEl && pvElFont > 0 ? pvEl.getBoundingClientRect().height / pvElFont : 1.4;
        const pvBlockH = Math.ceil(pvFontSize * pvLineRatio * maxPvLines);
        const imgMaxH = Math.floor(Math.max(0, rowH - pvBlockH - cellGapPx));

        grid = {
          imgMaxH,
          lpImgMaxH: imgMaxH,
          pvFontSize,
        };
      } else {
        const sectionGap = lp;
        const cellInternalGap = Math.round(lp * 0.1);
        const pvLineHeight = 1.3;
        const topMaxLines = maxPayLines(symbolGridData.top);
        const lowMaxLines = maxPayLines(symbolGridData.low);

        const designPvFont = Math.round(chrome.base * 0.84);
        const designRowBudget = Math.round(
          Math.max(0, (availH - sectionGap) / 2),
        );
        const designUsableRowH = Math.max(0, designRowBudget - cellInternalGap);

        const gridLandscapeImgMul =
          SYMBOL_GRID_CONFIG.landscapeImgHeightMul ?? 0.72;
        const designImgMaxH = Math.round(
          designUsableRowH * gridLandscapeImgMul,
        );

        const designRow1H =
          designImgMaxH +
          cellInternalGap +
          designPvFont * pvLineHeight * topMaxLines;
        const designRow2H =
          designImgMaxH +
          cellInternalGap +
          designPvFont * pvLineHeight * lowMaxLines;
        const designTotalH = designRow1H + sectionGap + designRow2H;

        const smallLandscapeGridMul = isSmallMobileLandscape
          ? (SYMBOL_GRID_CONFIG.smallMobileLandscapeScaleMul ?? 1)
          : 1;
        const fillScaleCap = SYMBOL_GRID_CONFIG.landscapeFillScaleCap ?? 1.3;

        // Width fit — the height fill alone lets symbols balloon on tall/square
        // viewports so the nowrap rows overflow. A row is a grid of 1fr
        // columns inside landscapeRowMaxWidthPct of the slide, so every cell is
        // as wide as its widest one: the picture or its pay-value text, which
        // both scale with `scale`. The five-symbol low row is the binding one.
        const sectionCellGap = Math.round(lp * 1.5); // matches .spt-grid-section gap
        const rowAvailW =
          availW * ((SYMBOL_GRID_CONFIG.landscapeRowMaxWidthPct ?? 100) / 100);
        ctx.font = `500 ${designPvFont}px 'Noto Sans', sans-serif`;
        const cellDesignW = (sym) => {
          const aspect = sym.imgW > 0 && sym.imgH > 0 ? sym.imgW / sym.imgH : 1;
          const textW = Math.max(
            0,
            ...sym.payValues.map(
              (pv) =>
                ctx.measureText(`${pv.count}: ${pv.payout} ${currency || ""}`)
                  .width,
            ),
          );
          return Math.max(designImgMaxH * aspect, textW);
        };
        const widthScaleForRow = (cells) => {
          const n = cells.length;
          if (n === 0 || designImgMaxH <= 0) return Infinity;
          const widest = Math.max(...cells.map(cellDesignW));
          if (widest <= 0) return Infinity;
          const avail = Math.max(0, rowAvailW - sectionCellGap * (n - 1));
          return avail / (n * widest);
        };
        const widthScale = Math.min(
          widthScaleForRow(symbolGridData.top),
          widthScaleForRow(symbolGridData.low),
        );

        const scale =
          designTotalH > 0
            ? Math.min(fillScaleCap, availH / designTotalH, widthScale) *
              smallLandscapeGridMul
            : smallLandscapeGridMul;

        const imgMaxH = Math.round(designImgMaxH * scale);
        const pvFontSize = Math.round(designPvFont * scale);

        grid = { imgMaxH, lpImgMaxH: imgMaxH, pvFontSize };
      }
    }

    // Only update state if values changed (prevents infinite loop)
    setSlideSizes((prev) => {
      const next = { special, grid };
      if (prev) {
        const same = JSON.stringify(prev) === JSON.stringify(next);
        if (same) return prev;
      }
      return next;
    });
  }, [
    shouldRender,
    dimensions.width,
    dimensions.height,
    isPortrait,
    descBaseFontSize,
    chrome,
    device,
    layoutDevice,
    isMobileSmall,
    isSmallMobileLandscape,
    isTunedStandardPortrait,
    isTunedMobileLandscape,
    isSmallMobilePortrait,
    landscapeFixedDescFont,
    smallMobileLandscapeDescMul,
    uniformDescFontMul,
    uniformFeatureSubtitleMul,
    uniformDescFontSize,
    t,
    symbolGridData,
    specialCardPays,
    currency,
    getSlideDescFontMul,
    getUniformFeatureSubtitleH,
    measureTick,
  ]);

  const slides = useMemo(() => {
    const resolveImgSrc = (item) =>
      item.symbolName
        ? SpineTextureExtractor.getTrimmedDataURL(item.symbolName) ||
          spineDataURLs[item.symbolName]?.src
        : item.assetName
          ? gameImages[item.assetName]
          : item.img;

    const renderPayValues = (payValues, fontSize) => (
      <div className="spt-grid-values">
        {payValues.map((pv, pvIdx) => (
          <span
            className="spt-grid-pv"
            key={pvIdx}
            style={{ fontSize: `${fontSize}px` }}
          >
            {pv.count}: {pv.payout} {currency || ""}
          </span>
        ))}
      </div>
    );

    // ── Wild & Scatters: one card per symbol ──
    const renderSpecialSymbols = () => {
      const sizes = slideSizes?.special || {
        imgH: 100,
        fontSize: Math.round(
          descBaseFontSize *
            getSlideDescFontMul(SPECIAL_SYMBOL_CONFIG.descFontMul),
        ),
      };
      const imgScaleMul = SPECIAL_SYMBOL_CONFIG.imgScaleMul ?? 1;
      const maxImgH = Math.max(
        ...SPECIAL_SYMBOL_CARDS.map((s) => {
          const sc =
            ((typeof s.imgScale === "object"
              ? getImgWidth(s.imgScale, layoutDevice, isPortrait)
              : s.imgScale) || 1) * imgScaleMul;
          return Math.round(sizes.imgH * sc);
        }),
      );
      return (
        <div
          className="spt-symbol-sections spt-symbol-cards"
          ref={specialSectionsRef}
        >
          {SPECIAL_SYMBOL_CARDS.map((s, si) => {
            const imgSrc = resolveImgSrc(s);
            const pays = specialCardPays[si] ?? [];
            const descW =
              !isTunedMobileLandscape && !isPortrait && sizes.descWidths
                ? sizes.descWidths[si]
                : undefined;
            return (
              <div className="spt-symbol-section" key={si}>
                <div
                  className="spt-symbol-img-wrap"
                  style={{ height: maxImgH }}
                >
                  {imgSrc && (
                    <img
                      className="spt-symbol-img"
                      src={imgSrc}
                      alt={t(s.subtitle)}
                      style={{ height: "100%", objectFit: "contain" }}
                    />
                  )}
                </div>
                <div className="spt-feature-subtitle">{t(s.subtitle)}</div>
                {pays.length > 0 && renderPayValues(pays, sizes.fontSize)}
                <p
                  className="spt-feature-desc"
                  style={{ fontSize: `${sizes.fontSize}px`, width: descW }}
                >
                  {t(s.desc)}
                </p>
              </div>
            );
          })}
        </div>
      );
    };

    // ── Symbols grid: top row + low row ──
    const renderSymbolGrid = () => {
      const sizes = slideSizes?.grid || {
        imgMaxH: 60,
        lpImgMaxH: 50,
        pvFontSize: 12,
      };
      const lowIds = new Set(symbolGridData.low.map((sym) => sym.id));

      const renderGridCell = (sym, symIdx) => {
        const imgH = lowIds.has(sym.id) ? sizes.lpImgMaxH : sizes.imgMaxH;
        return (
          <div className="spt-grid-cell" key={symIdx}>
            <div className="spt-grid-img-wrap" style={{ height: imgH }}>
              {sym.imgSrc && (
                <img
                  className="spt-grid-img"
                  src={sym.imgSrc}
                  alt={sym.name}
                  style={{ maxHeight: imgH, objectFit: "contain" }}
                />
              )}
            </div>
            {renderPayValues(sym.payValues, sizes.pvFontSize)}
          </div>
        );
      };

      if (isPortrait) {
        const { twoCol, threeCol } = splitPortraitGrid([
          ...symbolGridData.top,
          ...symbolGridData.low,
        ]);
        return (
          <div className="spt-grid-content" ref={gridContentRef}>
            {twoCol.length > 0 && (
              <div className="spt-grid-block spt-grid-block-2col">
                {twoCol.map(renderGridCell)}
              </div>
            )}
            {threeCol.length > 0 && (
              <div className="spt-grid-block spt-grid-block-3col">
                {threeCol.map(renderGridCell)}
              </div>
            )}
          </div>
        );
      }

      return (
        <div className="spt-grid-content" ref={gridContentRef}>
          {[symbolGridData.top, symbolGridData.low].map((section, si) => (
            <div
              className={`spt-grid-section ${si === 1 ? "spt-grid-lp" : "spt-grid-mp"}`}
              key={si}
            >
              {section.map(renderGridCell)}
            </div>
          ))}
        </div>
      );
    };

    // ── Feature slide: subtitle, picture, description ──
    const renderFeature = (fi) => {
      const f = FEATURE_SLIDES[fi];
      const featureData = (featureFontSizes && featureFontSizes[fi]) || {
        fontSize: Math.round(
          descBaseFontSize * getSlideDescFontMul(f.descFontMul),
        ),
        descWidths: [],
      };
      // descFontMul is baked into the fit effect — use the returned size directly.
      const descFontSize = featureData.fontSize;
      const portraitImgScale = isPortrait ? 0.8 * (f.portraitImgMul ?? 1) : 1;
      const landscapeImgScale =
        isTunedMobileLandscape && f.landscapeImgMul != null
          ? f.landscapeImgMul
          : 1;
      const imgScale = isPortrait ? portraitImgScale : landscapeImgScale;
      // Portrait is always the column; `stackedAlways` opts a slide into it on
      // landscape and desktop as well. Otherwise landscape is image-left /
      // text-right.
      const stacked = isPortrait || f.stackedAlways === true;
      const isLandscapeStacked = stacked && !isPortrait;

      // `imgWidth` is a percentage OF ITS WRAPPER, which is 50% of the slide in
      // the row layout but 100% in the column — halve it there so the picture
      // keeps its size.
      const stackedWidthFactor = isLandscapeStacked ? 0.5 : 1;
      const imgWidth = `calc(${getImgWidth(f.imgWidth, layoutDevice, isPortrait)} * ${imgScale} * ${stackedWidthFactor})`;
      const descMarginTop = f.descMarginTop
        ? getImgWidth(f.descMarginTop, layoutDevice, isPortrait)
        : undefined;
      const imgMarginTop = f.imgMarginTop
        ? getImgWidth(f.imgMarginTop, layoutDevice, isPortrait)
        : undefined;
      const featureDesc = t(f.desc);
      const featureImgSrc = resolveImgSrc(f);
      const descStyle = {
        fontSize: `${descFontSize}px`,
        ...(descMarginTop ? { marginTop: descMarginTop } : {}),
      };
      // `imgFillsBand` (portrait only) hands the whole band to the picture and
      // lets `object-fit: contain` pick the largest size that fits. A percent
      // of the wrapper; `true` means 100.
      const imgFillPct =
        isPortrait && f.imgFillsBand
          ? f.imgFillsBand === true
            ? 100
            : f.imgFillsBand
          : null;
      const featureImgStyle =
        imgFillPct != null
          ? { width: `${imgFillPct}%`, height: `${imgFillPct}%` }
          : { width: imgWidth };

      const imgWrapStyle = imgMarginTop ? { marginTop: imgMarginTop } : undefined;
      // Per-slide gaps in the portrait column — applied to the title-to-body
      // and the image-to-text gaps so the column stays evenly spaced.
      const portraitStackedGap =
        isPortrait && f.portraitBodyGapMul != null
          ? { gap: Math.round(linePad * f.portraitBodyGapMul) }
          : undefined;

      const featureBodyStyle =
        !isPortrait && f.landscapeBodyGapMul != null
          ? { gap: Math.round(linePad * f.landscapeBodyGapMul) }
          : undefined;

      if (stacked) {
        return (
          <div
            className={`spt-feature-content spt-feature-stacked${
              isLandscapeStacked ? " spt-feature-stacked-landscape" : ""
            }`}
          >
            <div
              className="spt-feature-stacked-inner"
              style={{
                // Same rule as `.spt-feature-text-wrap`'s padding-top, so a
                // column title lines up with a row title. If one moves, move both.
                ...(isLandscapeStacked
                  ? {
                      paddingTop: isTunedMobileLandscape
                        ? 0
                        : Math.round(linePad * 1.5),
                    }
                  : {}),
                ...portraitStackedGap,
              }}
              ref={(el) => {
                featureBodyRefs.current[fi] = el;
              }}
            >
              <div className="spt-feature-subtitle">{t(f.subtitle)}</div>
              <div
                className="spt-feature-stacked-body"
                style={portraitStackedGap}
              >
                <div className="spt-feature-img-wrap" style={imgWrapStyle}>
                  <img
                    className="spt-feature-img"
                    src={featureImgSrc}
                    alt={t(f.subtitle)}
                    style={featureImgStyle}
                  />
                </div>
                <p className="spt-feature-desc" style={descStyle}>
                  {featureDesc}
                </p>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className="spt-feature-content">
          <div
            className="spt-feature-body"
            style={featureBodyStyle}
            ref={(el) => {
              featureBodyRefs.current[fi] = el;
            }}
          >
            <div className="spt-feature-img-wrap" style={imgWrapStyle}>
              <img
                className="spt-feature-img"
                src={featureImgSrc}
                alt={t(f.subtitle)}
                style={{ width: imgWidth }}
              />
            </div>
            <div
              className="spt-feature-text-wrap"
              style={{
                ...(!isTunedMobileLandscape && f.textPadMul != null
                  ? {
                      paddingTop: Math.round(
                        Math.round(chrome.title * 0.5) * f.textPadMul,
                      ),
                    }
                  : {}),
                ...(f.subtitleNoWrap ? { overflow: "visible" } : {}),
              }}
            >
              <div
                className="spt-feature-subtitle"
                style={f.subtitleNoWrap ? { whiteSpace: "nowrap" } : undefined}
              >
                {t(f.subtitle)}
              </div>
              <p className="spt-feature-desc" style={descStyle}>
                {featureDesc}
              </p>
            </div>
          </div>
        </div>
      );
    };

    return PAYTABLE_SLIDES.map((slide) => () => (
      <div
        className={`spt-slide-content${slide.featureIndex != null ? " spt-slide-content-spaced" : ""}`}
        key={`slide-${slide.id}`}
      >
        <div className="spt-slide-title">{t(slide.title)}</div>
        {slide.id === SLIDE_IDS.WILD_SCATTERS && renderSpecialSymbols()}
        {slide.id === SLIDE_IDS.SYMBOLS && renderSymbolGrid()}
        {slide.featureIndex != null && renderFeature(slide.featureIndex)}
        {slide.id === SLIDE_IDS.PAYLINES && (
          <img
            className="spt-paylines-img"
            src={paylineImgSrc}
            alt={t(slide.title)}
          />
        )}
      </div>
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paylineImgSrc,
    device,
    layoutDevice,
    isPortrait,
    featureFontSizes,
    descBaseFontSize,
    slideSizes,
    symbolGridData,
    specialCardPays,
    currency,
    t,
  ]);

  // ── Memoized overlay stylesheet — shell + slide content styles ──
  const overlayStyles = useMemo(() => {
    const symbolSectionsGap = isTunedMobileLandscape
      ? linePad
      : isPortrait
        ? Math.round(linePad * 1.6)
        : Math.round(linePad * 1.5);
    const symbolSectionFlex = isTunedMobileLandscape
      ? "1 1 0"
      : isPortrait
        ? "0 0 auto"
        : "1";
    const symbolCardsJustify = isTunedMobileLandscape
      ? "center"
      : "flex-start";
    const featureImgWrapPadRight = isTunedMobileLandscape
      ? 0
      : Math.round(linePad * 0.8);
    // ── How much of the landscape column the IMAGE may take ──
    //
    // Percent of the space left after the title and the description. The image
    // wrapper is a flex child that would otherwise absorb ALL of it, and with
    // "object-fit: contain" the picture then sits in a box far taller than
    // itself — space that reads as empty because nothing is drawn in it.
    //
    // The image FILLS this wrapper (height 100%), so this number alone decides
    // how much room the picture gets — lower it and the image shrinks with it,
    // the slack collecting under the description. 85 -> 72 -> 61.
    //
    // Landscape only — portrait sizes the image from its own height and is not
    // affected.
    const STACKED_LANDSCAPE_IMG_MAX_H = 61;

    // ── The PORTRAIT image band, shared by every feature slide ──
    //
    // Percent of the body the image wrapper occupies, FIXED — not sized to the
    // picture inside it, so the description sits on the same line on every
    // feature slide whatever the shape of its picture. The picture is centred
    // inside the band and capped to it, so a small image stays small.
    const STACKED_PORTRAIT_IMG_BAND = 52;

    // ── The Wild & Scatters cards' internal gap, matched to the feature slides ──
    //
    // Portrait lines it up with the feature column (1.5 x linePad) so the whole
    // paytable breathes alike; landscape keeps 0.4, where the cards sit side by
    // side and have far less height to give away.
    //
    // Keep this in step with `portraitBodyGapMul` on the feature slides.
    const symbolSectionGapMul = isPortrait ? 1.5 : 0.4;

    // NOTE: the landscape-stacked branch in the render recomputes this exact
    // rule, so a slide in the column keeps its title on the same line as one in
    // the row. Change one and change the other.
    const featureTextWrapPadTop = isTunedMobileLandscape
      ? 0
      : Math.round(linePad * 1.5);
    const featureTextWrapPadLeft = isTunedMobileLandscape
      ? 0
      : Math.round(linePad * 0.5);
    const featureTextWrapGap = isTunedMobileLandscape
      ? Math.round(linePad * 0.4)
      : Math.round(linePad * 0.5);
    const featureImgWrapJustify = isTunedMobileLandscape
      ? "center"
      : "flex-end";
    const featureBodyOverflow = isTunedMobileLandscape ? "visible" : "hidden";
    const featureImgWrapOverflow = isTunedMobileLandscape
      ? "visible"
      : "hidden";
    const symbolDescPadH = isTunedMobileLandscape
      ? 0
      : Math.round(chrome.base * 0.3);
    // One gap in both directions, and between both blocks: the 2-col grid used
    // 0.3 between rows against 0.7 between columns, so the same slide had two
    // different rhythms.
    const gridPortraitRowGap = Math.round(linePad * 0.7);
    // ── The two COLUMNS get double the row gap, on phones only ──
    //
    // Rows were fine; the two columns sat too close together. Doubling the
    // horizontal gap alone spreads them without touching the vertical rhythm —
    // and the vertical is what the height fit is budgeted against, so leaving
    // it alone keeps the grid inside the slide.
    //
    // STANDARD AND SMALL MOBILE PORTRAIT ONLY, deliberately not `isPortrait`:
    // that would take tablet portrait with it, where the grid has plenty of
    // width already and the extra gap only flings the columns to the edges.
    const isPhonePortrait = isStandardMobilePortrait || isSmallMobilePortrait;
    const gridPortraitColGap = Math.round(
      linePad * (isPhonePortrait ? 1.4 : 0.7),
    );
    const gridContentGap = isPortrait ? gridPortraitRowGap : linePad;
    const gridCellGap = Math.round(linePad * 0.1);
    return `
    .spt-overlay { font-family: 'Noto Sans', sans-serif; }

    /* ── HEADER ── */
    .spt-header {
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      padding-bottom: ${headerPaddingBottom}px;
      position: relative;
      z-index: 1;
    }
    .spt-header-title-row {
      position: relative;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .spt-title {
      flex: 1;
      font-size: ${headerTitleFontSize}px; font-weight: 700;
      color: #ffffff; margin: 0; text-align: center; white-space: nowrap;
      text-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
    }
    .spt-separator {
      ${
        isTunedShellPortrait
          ? `position: relative;
      margin-top: ${separatorMarginTop}px;
      margin-bottom: ${separatorMarginBottom}px;`
          : `position: absolute;
      bottom: 0; left: 50%; transform: translateX(-50%);`
      }
      width: ${separatorLineWidth}px;
      height: ${separatorHeight}px;
      background: linear-gradient(to right, transparent, #ffc102 35%, #ffc102 65%, transparent);
      clip-path: ellipse(50% 50% at 50% 50%);
    }
    .spt-close-btn {
      position: absolute;
      top: 50%; transform: translateY(-50%);
      right: ${closeButtonRight}px;
      width: ${closeButtonSize}px; height: ${closeButtonSize}px;
      background-color: transparent; border: none; cursor: pointer; padding: 0;
      background-size: contain; background-repeat: no-repeat;
      background-position: center; transition: filter 0.2s ease;
      z-index: 1010;
    }
    .spt-close-btn:hover { filter: brightness(2.0); }

    /* Decorative glow — standard mobile portrait */
    .spt-glow {
      position: absolute;
      top: ${glowTop};
      left: 50%;
      transform: translateX(-50%);
      width: ${glowWidth};
      pointer-events: none;
      z-index: 0;
      opacity: 1;
    }
    /* ── SLIDE CONTENT — base layout for all slide types ── */
    .spt-slide-content {
      flex: 1; min-height: 0;
      width: 100%;
      max-width: ${
        (isTabletPortrait ||
          isSmallMobilePortrait ||
          isStandardMobilePortrait) &&
        shellContentWidth != null
          ? `${shellContentWidth}px`
          : isPortrait
            ? `calc(100% - ${arrowSafeZone}px)`
            : "100%"
      };
      min-width: 0;
      margin: 0;
      box-sizing: border-box;
      display: flex; flex-direction: column;
      align-items: center; justify-content: flex-start;
      gap: ${slideContentGap}px;
      position: relative;
      z-index: 1;
    }
    .spt-slide-content-spaced {
      gap: ${slideContentSpacedGap}px;
    }
    .spt-slide-title {
      font-size: ${slideTitleFontSize}px; font-weight: bold;
      color: #ffc102; text-align: center;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
      margin-bottom: 0;
    }

    /* ── Slide content: Paylines ── */
    .spt-paylines-img {
      flex: 1;
      min-height: 0;
      width: 100%;
      object-fit: contain;
    }

    /* ── Slide content: Features ── */
    .spt-feature-content {
      flex: 1;
      min-height: 0;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
    }
    .spt-feature-subtitle {
      font-size: ${featureSubtitleFontSize}px;
      font-weight: 700;
      color: #ffffff;
      text-align: center;
      text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
      margin: 0;
      flex-shrink: 0;
    }
    .spt-feature-body {
      flex: 1;
      min-height: 0;
      overflow: ${featureBodyOverflow};
      width: 100%;
      display: flex;
      flex-direction: row;
      align-items: stretch;
      justify-content: flex-start;
    }
    .spt-feature-img-wrap {
      position: relative;
      width: 50%;
      height: 100%;
      min-height: 0;
      display: flex;
      align-items: flex-start;
      justify-content: ${featureImgWrapJustify};
      overflow: ${featureImgWrapOverflow};
      padding-right: ${featureImgWrapPadRight}px;
      box-sizing: border-box;
    }
    .spt-feature-text-wrap {
      width: 50%;
      height: 100%;
      padding-top: ${featureTextWrapPadTop}px;
      padding-left: ${featureTextWrapPadLeft}px;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      gap: ${featureTextWrapGap}px;
      overflow: hidden;
    }
    .spt-feature-img {
      object-fit: contain;
      max-height: 100%;
      /* iOS: pin to own pixel-aligned layer so the scaled WebP's bottom edge
         doesn't render a faint 1px seam line against the dark backdrop */
      -webkit-backface-visibility: hidden;
      backface-visibility: hidden;
      transform: translateZ(0);
    }
    .spt-feature-desc {
      /* font-size set inline via descFontSizes */
      font-weight: 500;
      -webkit-text-stroke: 0.3px rgba(255,255,255,0.5);
      color: #ffffff;
      text-align: center;
      line-height: 1.4;
      white-space: pre-line;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
      margin: 0;
      overflow-wrap: break-word;
    }
    .spt-feature-stacked {
      justify-content: flex-start;
    }
    .spt-feature-stacked-inner {
      flex: 1;
      min-height: 0;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      gap: ${linePad}px;
      overflow: hidden;
    }
    .spt-feature-stacked-body {
      flex: 1;
      min-height: 0;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      gap: ${linePad}px;
      overflow: hidden;
    }
    .spt-feature-stacked .spt-feature-img-wrap {
      display: flex;
      align-items: center;
      justify-content: center;
      /* A BAND of declared height, not a wrapper that hugs its picture — see
         STACKED_PORTRAIT_IMG_BAND. This is what puts the description on the
         same line on every feature slide. */
      flex: 0 0 ${STACKED_PORTRAIT_IMG_BAND}%;
      width: 100%;
      padding-right: 0;
    }
    .spt-feature-stacked .spt-feature-img {
      object-fit: contain;
      /* Capped to the band and centred in it, so a smaller picture stays
         smaller instead of stretching to fill the slot. */
      max-height: 100%;
    }
    /* ── The column in LANDSCAPE has to fit, not overflow ──
       Portrait can let the image run at its natural height: the column is tall
       and "max-height: none" with "flex-shrink: 0" is what keeps it crisp.
       Landscape is short, so the same rules push the image past the body and
       "overflow: hidden" cuts it — which is exactly what was happening.
       Here the wrapper is allowed to SHRINK and the image is capped to it;
       "object-fit: contain" then letterboxes rather than crops, so the whole
       picture is always visible whatever height is left over.
       NOTE: no backticks in this block — it is inside a template literal. */
    .spt-feature-stacked-landscape .spt-feature-img-wrap {
      flex: 1 1 auto;
      min-height: 0;
      max-height: ${STACKED_LANDSCAPE_IMG_MAX_H}%;
    }
    .spt-feature-stacked-landscape .spt-feature-img {
      /* FILL the wrapper vertically rather than merely being capped by it.
         With "max-height" alone the picture stops at whatever its width allows
         and leaves slack inside the wrapper — so the wrapper reads as the empty
         space, not the image. Height 100% makes the box match the wrapper and
         "object-fit: contain" keeps the aspect, letterboxing instead of
         stretching. The WRAPPER is now the only thing that decides how much
         room the image gets. */
      height: 100%;
      max-height: 100%;
    }
    /* ── No gaps in the landscape column ──
       The image element now takes the whole box the flex row leaves it, and
       "object-fit: contain" centres the picture inside that box — so the
       element is already taller than what is drawn, and that letterboxing IS
       the breathing room. Adding linePad above and below it on top of that is
       what left the slide looking half empty.
       Portrait keeps its gaps: there the image is sized to its own height, so
       nothing separates it from the title and the text but these. */
    .spt-feature-stacked-landscape .spt-feature-stacked-inner,
    .spt-feature-stacked-landscape .spt-feature-stacked-body {
      gap: 0;
    }
    /* The row layout capped the description for free — its text wrapper is 50%
       of the slide. The column has no wrapper, so it needs saying. Kept in step
       with the fit measurement above; see the constant's note. */
    .spt-feature-stacked-landscape .spt-feature-desc {
      max-width: ${STACKED_LANDSCAPE_DESC_MAX_W_PCT}%;
    }


    /* ── Slide content: Symbol sections ── */
    .spt-symbol-sections {
      flex: 1;
      min-height: 0;
      width: 100%;
      display: flex;
      flex-direction: ${isPortrait ? "column" : "row"};
      align-items: ${isPortrait ? "center" : "flex-start"};
      justify-content: center;
      gap: ${symbolSectionsGap}px;
      overflow: hidden;
    }
    .spt-symbol-cards {
      justify-content: ${symbolCardsJustify};
    }
    /* Wild & Scatters cards: HALF the gap between picture, title, pay values
       and text. */
    .spt-symbol-cards .spt-symbol-section {
      gap: ${Math.round((linePad * symbolSectionGapMul) / 2)}px;
    }
    /* No clipping on the symbol slides — a picture or a line of text is never
       cut off (Vladimir, 2026-09-30: "nor text nor images must not have this
       cutout of overflow hidden"). */
    .spt-symbol-cards,
    .spt-symbol-cards .spt-symbol-section {
      overflow: visible;
    }
    /* PORTRAIT ONLY — each section an equal share of the slide, and the
       picture takes ALL the height its section has left after the title and
       the text (the measured height left the symbols at a fraction of it;
       Vladimir, 2026-09-30). Not in landscape: the sections sit side by side
       at their content's height there, so a picture told to fill "the rest"
       collapsed to nothing (the landscape bug, 2026-09-30) — landscape keeps
       the measured height. Plus half the gap BETWEEN the sections. */
    ${isPortrait ? `
    .spt-symbol-cards {
      gap: ${Math.round(symbolSectionsGap / 2)}px;
    }
    .spt-symbol-cards .spt-symbol-section {
      flex: 1 1 0;
    }
    .spt-symbol-cards .spt-symbol-img-wrap {
      flex: 1 1 0;
      min-height: 0;
      height: auto !important;
    }` : ""}
    .spt-symbol-section {
      flex: ${symbolSectionFlex};
      min-height: 0;
      ${isPortrait ? "width: 100%;" : "min-width: 0;"}
      ${isTunedMobileLandscape && !isPortrait ? "width: 0;" : ""}
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: ${isPortrait ? "center" : "flex-start"};
      text-align: center;
      gap: ${Math.round(linePad * symbolSectionGapMul)}px;
      overflow: hidden;
    }
    .spt-symbol-img-wrap {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      /* height set inline by symbolSlide1Sizes */
    }
    .spt-symbol-img {
      max-height: 100%;
      max-width: 100%;
      object-fit: contain;
    }
    .spt-symbol-section .spt-feature-desc {
      padding: 0 ${symbolDescPadH}px;
    }
    .spt-symbol-section .spt-feature-subtitle {
      white-space: nowrap;
    }

    /* ── Slide content: Symbols grid ── */
    .spt-grid-content {
      flex: 1; min-height: 0;
      width: 100%;
      display: flex;
      flex-direction: column;
      /* WIDTH STAYS 100%. The sizing pass measures this element's clientWidth
         to work out how large the symbols may be, so a content-driven width
         here is a shrink loop: narrower box, smaller symbols, narrower box.
         The rows below control their own spread instead. */
      align-items: stretch;
      justify-content: center;
      gap: ${gridContentGap}px;
    }
    .spt-grid-block {
      justify-content: center;
      width: 100%;
    }
    .spt-grid-block-2col {
      display: grid;
      grid-template-columns: auto auto;
      justify-items: center;
      row-gap: ${gridPortraitRowGap}px;
      column-gap: ${gridPortraitColGap}px;
    }
    .spt-grid-block-3col {
      display: flex;
      flex-wrap: nowrap;
      gap: ${gridPortraitColGap}px;
    }
    .spt-grid-block-3col .spt-grid-cell {
      flex: 0 0 auto;
    }
    .spt-grid-section {
      /* Landscape-only element. A GRID, not a flex row, and that is the fix:
         1fr columns are all the same width, so the two rows line up.

         Flex could not do it. Cells sized to their own content, and the low-pay
         cards are narrower than the professor and the gadgets, so the bottom row
         drew narrower. flex:1 fixed that by stretching them across the full
         width, which pushed them too far apart.

         The WIDTH comes from the parent (see spt-grid-content): both rows are
         handed the same one, so their columns land on top of each other and the
         block is only as wide as its widest row. Never wraps, so the 2-row
         layout is fixed and symbols only scale via the height fit. */
      display: grid;
      grid-auto-flow: column;
      grid-auto-columns: 1fr;
      /* Both rows fill the same parent width, so their 1fr columns land on top
         of each other. landscapeRowMaxWidthPct pulls them in from the full
         span so they are not flung to the edges. The landscape width fit reads
         the same number, so change it in SYMBOL_GRID_CONFIG only. */
      width: 100%;
      max-width: ${SYMBOL_GRID_CONFIG.landscapeRowMaxWidthPct}%;
      margin: 0 auto;
      align-items: center;
      justify-items: center;
      justify-content: center;
      gap: ${Math.round(linePad * 1.5)}px;
    }
    .spt-grid-section.spt-grid-mp {
      justify-content: center;
      align-items: ${isPortrait ? "center" : "flex-start"};
    }
    .spt-grid-section.spt-grid-lp {
      flex-direction: row;
      flex-wrap: nowrap;
      align-items: ${isPortrait ? "center" : "flex-start"};
      justify-content: center;
    }
    .spt-grid-cell {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      gap: ${gridCellGap}px;
    }

    .spt-grid-img-wrap {
      /* height set inline via symbolSlide3Sizes.imgMaxH */
      flex-shrink: 0;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .spt-grid-img {
      max-height: 100%;
      max-width: 100%;
      object-fit: contain;
    }
    .spt-grid-values {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0;
      flex-shrink: 0;
    }
    .spt-grid-pv {
      /* ── The SAME face as a slide description, only smaller ──
         Weight, colour, stroke and shadow are copied from .spt-feature-desc so
         the paytable reads as one typeface throughout. Only the SIZE differs,
         and it is set inline from symbolSlide3Sizes.pvFontSize because this
         grid fits itself to the space it has.

         These used to be weight 600 at 80% white against the descriptions'
         500 at full white with a hairline stroke — heavier AND dimmer, which
         is why no single weight ever matched them.

         line-height stays 1.3, NOT the description's 1.4: the portrait height
         budget (pvBlockH) is computed from this number, so changing it here
         silently changes how much room the grid thinks it has. */
      font-weight: 500;
      color: #ffffff;
      -webkit-text-stroke: 0.3px rgba(255,255,255,0.5);
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
      line-height: 1.3;
      white-space: nowrap;
    }
  `;
  }, [
    chrome,
    linePad,
    slideContentGap,
    slideContentSpacedGap,
    isPortrait,
    isTunedMobileLandscape,
    isTabletPortrait,
    isStandardMobilePortrait,
    isSmallMobilePortrait,
    isTunedShellPortrait,
    shellContentWidth,
    arrowSafeZone,
    separatorLineWidth,
    separatorHeight,
    closeButtonSize,
    closeButtonRight,
    separatorMarginTop,
    separatorMarginBottom,
    headerPaddingBottom,
    headerTitleFontSize,
    slideTitleFontSize,
    featureSubtitleFontSize,
    glowTop,
    glowWidth,
  ]);

  if (!shouldRender) return null;

  const closeIconSrc = getUITextureAsDataURL("button_x");

  return (
    <div
      ref={overlayRef}
      className="spt-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        // Visible-area size (excludes browser toolbars). 100vw/100vh on iOS
        // Chrome resolve to the taller layout viewport, which pushes the
        // footer/pagination behind the bottom toolbar. dimensions.* come from
        // visualViewport (the real visible area) and match the fit math used
        // everywhere else in the overlay.
        width: dimensions.width,
        height: dimensions.height,
        background:
          "radial-gradient(ellipse at center, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.92) 75%, rgba(0,0,0,0.97) 100%)",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        padding: `${shellTopPad}px 0 ${shellBottomPad}px 0`,
        boxSizing: "border-box",
        opacity: 0,
        // When the iOS fullscreen overlay is up, hide the paytable for real:
        // opacity alone leaves the subtree hit-testable (the active slide sets
        // pointer-events:auto, defeating the root's `none`), so it sits on top of
        // the canvas and swallows the fullscreen swipe. visibility:hidden can't be
        // overridden by a descendant, so the whole subtree stops hit-testing.
        visibility: isHidden ? "hidden" : "visible",
        pointerEvents: isHidden ? "none" : "auto",
      }}
    >
      {/* ── Decorative glow — standard mobile portrait only ── */}
      {isTunedStandardPortrait && (
        <img
          className="spt-glow"
          src={gameImages.glow || ""}
          alt=""
          aria-hidden="true"
        />
      )}

      {/* ── HEADER: title + close + separator ── */}
      <div className="spt-header">
        <div className="spt-header-title-row">
          <h1 className="spt-title">
            {t("paytable_title")}
          </h1>
          <button
            className="spt-close-btn"
            onClick={handleClose}
            style={{
              backgroundImage: closeIconSrc ? `url('${closeIconSrc}')` : "none",
            }}
          />
        </div>
        <div className="spt-separator" />
      </div>

      {/* ── CAROUSEL ── */}
      <PaytableSlider
        slides={slides}
        dimensions={dimensions}
        device={layoutDevice}
        orientation={orientation}
        chrome={chrome}
        toolbar={toolbar}
        isPortrait={isPortrait}
        shouldRender={shouldRender}
        contentWidth={
          isTabletPortrait ||
          isSmallMobilePortrait ||
          isStandardMobilePortrait ||
          isTunedMobileLandscape
            ? shellContentWidth
            : undefined
        }
        landscapeChromeSideOffset={tunedLandscapeChromeSideOffset}
        isTunedMobileLandscape={isTunedMobileLandscape}
        isWideDesktop={isWideDesktop}
        footerMarginTop={footerMarginTop}
        glowSrc={gameImages.glow}
        isHidden={isHidden}
      />

      {/* Overlay-scoped styles */}
      <style>{overlayStyles}</style>
    </div>
  );
};

export default PaytableOverlay;
